import { useState } from 'react';
import { Alert } from '@mui/material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { CheckHQResponse, WorkersV2API } from '@/services/workersV2API';

type Props = {
  workerId: number;
  checkhq: CheckHQResponse;
  onSync?: () => void;
};

const CheckHQCard = ({ workerId, checkhq, onSync }: Props) => {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const result = await WorkersV2API.syncFromCheckHQ(workerId);
      if (result.success) {
        setSyncResult({
          success: true,
          message: 'Sync completed successfully',
        });
        onSync?.();
      } else {
        setSyncResult({
          success: false,
          message: result.error || 'Sync failed',
        });
      }
    } catch (err) {
      setSyncResult({ success: false, message: 'Failed to sync from CheckHQ' });
    } finally {
      setSyncing(false);
    }
  };

  if (!checkhq.available) {
    return (
      <Card title="CheckHQ">
        <Card.Section>
          <Stack vertical gap={8}>
            <Badge label="Not Available" status="neutral" />
            <Small color="lighter">
              {'reason' in checkhq && checkhq.reason
                ? checkhq.reason
                : 'error' in checkhq && checkhq.error
                  ? checkhq.error
                  : 'No CheckHQ data available for this worker'}
            </Small>
            {'employee_id' in checkhq && checkhq.employee_id && (
              <Small color="lighter">Employee ID: {checkhq.employee_id}</Small>
            )}
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card
      actions={[
        {
          label: syncing ? 'Syncing...' : 'Sync',
          onAction: handleSync,
          disabled: syncing,
        },
      ]}
      title="CheckHQ"
    >
      {syncResult && (
        <Card.Section>
          <Alert
            severity={syncResult.success ? 'success' : 'error'}
            onClose={() => setSyncResult(null)}
          >
            {syncResult.message}
          </Alert>
        </Card.Section>
      )}

      <Card.Section>
        <pre
          style={{
            background: '#f5f5f5',
            padding: '16px',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px',
            lineHeight: '1.5',
            margin: 0,
          }}
        >
          {JSON.stringify(checkhq, null, 2)}
        </pre>
      </Card.Section>
    </Card>
  );
};

export default CheckHQCard;
